import { requiresRoomScope, type EditableField } from '../../lib/adminTemplates';
import type { AppliesTo, FieldType } from '../../lib/fieldValue';
import { FieldConfigEditor } from './FieldConfigEditor';

const FIELD_TYPES: FieldType[] = ['TIER', 'STARS', 'DURATION', 'RATIO', 'NUMBER', 'SHORT_TEXT', 'LONG_TEXT', 'IMAGE', 'LINK'];

const APPLIES_TO: { value: AppliesTo; label: string }[] = [
  { value: 'BOTH_ROWS', label: 'Player + Community' },
  { value: 'PLAYER_ONLY', label: 'Player only' },
  { value: 'COMMUNITY_ONLY', label: 'Community only' },
  { value: 'ROOM', label: 'Room (single value)' },
];

const inputClass =
  'min-h-tap rounded-md border border-border-strong bg-bg px-3 text-text disabled:cursor-not-allowed disabled:opacity-50';

function FieldError({ message }: { message: string | undefined }) {
  if (!message) {
    return null;
  }
  return (
    <p role="alert" className="telemetry text-signal">
      {message}
    </p>
  );
}

interface FieldRowProps {
  field: EditableField;
  errors: Record<string, string>;
  otherSections: { localId: string; name: string }[];
  isFirst: boolean;
  isLast: boolean;
  disabled: boolean;
  onUpdate: (patch: Partial<Omit<EditableField, 'localId'>>) => void;
  onChangeType: (type: FieldType) => void;
  onChangeAppliesTo: (appliesTo: AppliesTo) => void;
  onMove: (direction: 'up' | 'down') => void;
  onMoveToSection: (toSectionLocalId: string) => void;
  onRemove: () => void;
}

/** One field's editable row: key/label/type/appliesTo/allowFootnote/overlayGroup/exportColumn plus
 * its type-specific config editor, move/move-to-section/remove controls, and any server-side
 * `fields` validation errors rendered next to the offending input (WP10 spec). */
export function FieldRow({
  field,
  errors,
  otherSections,
  isFirst,
  isLast,
  disabled,
  onUpdate,
  onChangeType,
  onChangeAppliesTo,
  onMove,
  onMoveToSection,
  onRemove,
}: FieldRowProps) {
  const roomScoped = requiresRoomScope(field.type) || field.appliesTo === 'ROOM';

  return (
    <li className="rounded-md border border-border bg-bg px-4 py-3" style={{ viewTransitionName: `vt-${field.localId}` }}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Key</span>
            <input
              type="text"
              value={field.key}
              disabled={disabled}
              onChange={(event) => onUpdate({ key: event.target.value })}
              className={`telemetry ${inputClass}`}
            />
            <FieldError message={errors.key} />
          </label>
          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Label</span>
            <input
              type="text"
              value={field.label}
              disabled={disabled}
              onChange={(event) => onUpdate({ label: event.target.value })}
              className={inputClass}
            />
            <FieldError message={errors.label} />
          </label>

          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Type</span>
            <select
              value={field.type}
              disabled={disabled}
              onChange={(event) => onChangeType(event.target.value as FieldType)}
              className={inputClass}
            >
              {FIELD_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <FieldError message={errors.type} />
          </label>
          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Applies to</span>
            <select
              value={field.appliesTo}
              disabled={disabled || requiresRoomScope(field.type)}
              onChange={(event) => onChangeAppliesTo(event.target.value as AppliesTo)}
              className={inputClass}
            >
              {APPLIES_TO.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <FieldError message={errors.appliesTo} />
          </label>

          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Overlay group</span>
            <input
              type="text"
              value={field.overlayGroup ?? ''}
              disabled={disabled || roomScoped}
              placeholder={roomScoped ? 'n/a for Room fields' : 'Combat'}
              onChange={(event) => onUpdate({ overlayGroup: event.target.value })}
              className={inputClass}
            />
            <FieldError message={errors.overlayGroup} />
          </label>
          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Export column</span>
            <input
              type="text"
              value={field.exportColumn ?? ''}
              disabled={disabled || roomScoped}
              placeholder={roomScoped ? 'n/a for Room fields' : 'Damage'}
              onChange={(event) => onUpdate({ exportColumn: event.target.value })}
              className={inputClass}
            />
            <FieldError message={errors.exportColumn} />
          </label>

          <label className="flex min-h-tap items-center gap-2 text-sm text-text-muted sm:col-span-2">
            <input
              type="checkbox"
              checked={field.allowFootnote}
              disabled={disabled}
              onChange={(event) => onUpdate({ allowFootnote: event.target.checked })}
              className="size-4"
            />
            Allow footnote
          </label>

          <div className="sm:col-span-2">
            <FieldConfigEditor
              key={field.type}
              config={field.config}
              disabled={disabled}
              onChange={(config) => onUpdate({ config })}
            />
            <FieldError message={errors.config} />
          </div>
        </div>

        {!disabled && (
          <div className="flex shrink-0 flex-col gap-1.5">
            <button
              type="button"
              disabled={isFirst}
              onClick={() => onMove('up')}
              className="min-h-tap min-w-tap rounded-md border border-border-strong px-2 text-sm text-text-muted disabled:opacity-30"
              aria-label={`Move ${field.label || 'field'} up`}
            >
              ↑
            </button>
            <button
              type="button"
              disabled={isLast}
              onClick={() => onMove('down')}
              className="min-h-tap min-w-tap rounded-md border border-border-strong px-2 text-sm text-text-muted disabled:opacity-30"
              aria-label={`Move ${field.label || 'field'} down`}
            >
              ↓
            </button>
            {otherSections.length > 0 && (
              <select
                aria-label={`Move ${field.label || 'field'} to section`}
                value=""
                onChange={(event) => {
                  if (event.target.value !== '') {
                    onMoveToSection(event.target.value);
                  }
                }}
                className="min-h-tap rounded-md border border-border-strong bg-bg px-1 text-xs text-text-muted"
              >
                <option value="">Move to…</option>
                {otherSections.map((section) => (
                  <option key={section.localId} value={section.localId}>
                    {section.name || 'Untitled section'}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={onRemove}
              className="min-h-tap min-w-tap rounded-md border border-border-strong px-2 text-sm text-text-muted hover:border-signal hover:text-signal"
              aria-label={`Remove ${field.label || 'field'}`}
            >
              Remove
            </button>
          </div>
        )}
      </div>
    </li>
  );
}
